import React, { useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Print as PrintIcon, WarningAmber as WarningIcon } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import JsBarcode from 'jsbarcode';
import { useTranslation } from 'react-i18next';
import {
  A5_PORTRAIT_PAGE_STYLE,
  A5_PORTRAIT_PREVIEW_MAX_WIDTH,
  a5PortraitPrintSx,
} from '../utils/printPageStyles';
import { resolveCargoLogoSrc } from '../utils/trendyolPrintUtils';

const TY_ORANGE = '#F27A1A';
const TY_ORANGE_DARK = '#e65100';
const BORDER_COLOR = '#D1D5DB';
const TRENDYOL_LOGO_SRC = '/assets/Trendyol_mp_logo.png';
const TEXT_DARK = '#1a1a2e';
const TITLE_DARK = '#2c3e50';
const LABEL_GREY = '#6b7280';
const WARNING_RED = '#EF4444';
const LABEL_FONT = '"Segoe UI", Arial, Helvetica, sans-serif';

interface TrendyolPrintModalProps {
  open: boolean;
  onClose: () => void;
  order: any;
  companyId?: string;
}

const parseJsonObject = (raw: unknown): Record<string, any> => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return raw && typeof raw === 'object' ? (raw as Record<string, any>) : {};
};

const normalizeAddress = (raw: any) => {
  if (!raw || typeof raw !== 'object') return null;
  const pick = (...keys: string[]): string => {
    for (const key of keys) {
      const value = raw[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  };
  const firstLast = `${pick('firstName')} ${pick('lastName')}`.trim();
  return {
    name: pick('fullName') || firstLast,
    address: pick('address', 'fullAddress', 'address1', 'addressLine'),
    city: pick('city', 'cityName'),
    district: pick('district', 'town', 'townName', 'county'),
    neighborhood: pick('neighborhood', 'districtName'),
  };
};

const buildAddressFingerprint = (address: ReturnType<typeof normalizeAddress>) => {
  if (!address) return '';
  return [address.name, address.address, address.district, address.city, address.neighborhood]
    .join('|')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

const TrendyolPrintModal: React.FC<TrendyolPrintModalProps> = ({ open, onClose, order }) => {
  const { t } = useTranslation('salesSystem');
  const printRef = useRef<HTMLDivElement>(null);
  const [isPreview, setIsPreview] = useState(true);

  const parsedShippingAddress = useMemo(() => {
    if (!order) return {};
    const shippingAddress = order.shippingAddress;
    if (!shippingAddress) return {};
    if (typeof shippingAddress === 'string') {
      try {
        return JSON.parse(shippingAddress);
      } catch {
        return { address: shippingAddress };
      }
    }
    return shippingAddress;
  }, [order]);

  const parsedBillingAddress = useMemo(() => {
    if (!order) return {};
    const billingAddress = order.billingAddress;
    if (!billingAddress) return {};
    if (typeof billingAddress === 'string') {
      try {
        return JSON.parse(billingAddress);
      } catch {
        return { address: billingAddress };
      }
    }
    return billingAddress;
  }, [order]);

  const parsedCargoInfo = useMemo(() => {
    if (!order) return {};
    const cargoInfo = order.cargoInfo;
    if (!cargoInfo) return {};
    if (typeof cargoInfo === 'string') {
      try {
        return JSON.parse(cargoInfo);
      } catch {
        return {};
      }
    }
    return cargoInfo;
  }, [order]);

  const cargoBarcodeValue = useMemo(() => {
    if (!order) return '';
    return (
      parsedCargoInfo?.cargoBarcode ||
      parsedCargoInfo?.cargoSenderNumber ||
      parsedCargoInfo?.cargoTrackingNumber ||
      order.trackingNumber ||
      order.shipmentPackageId ||
      order.trendyolOrderNumber ||
      order.orderNumber ||
      order.id ||
      ''
    )
      .toString()
      .trim();
  }, [order, parsedCargoInfo]);

  const generateBarcode = (text: string) => {
    const canvas = document.createElement('canvas');
    try {
      JsBarcode(canvas, text, {
        format: 'CODE128',
        width: 2,
        height: 90,
        displayValue: false,
        margin: 8,
      });
      return canvas.toDataURL();
    } catch (error) {
      console.error('❌ [TRENDYOL PRINT MODAL] Barkod üretilemedi:', error);
      return '';
    }
  };

  const barcodeDataUrl = cargoBarcodeValue ? generateBarcode(cargoBarcodeValue) : '';

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Trendyol_Siparis_${order?.orderNumber || order?.id || t('ecommerceOrders.marketplaces.unknown')}`,
    pageStyle: A5_PORTRAIT_PAGE_STYLE,
    onBeforeGetContent: () => {
      setIsPreview(false);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setIsPreview(true);
    },
  });

  const shippingAddress = useMemo(() => normalizeAddress(parsedShippingAddress), [parsedShippingAddress]);
  const billingAddress = useMemo(() => normalizeAddress(parsedBillingAddress), [parsedBillingAddress]);

  const addressesDiffer = useMemo(() => {
    if (!shippingAddress || !billingAddress) return false;
    const billingHasContent = Boolean(billingAddress.name || billingAddress.address || billingAddress.city);
    if (!billingHasContent) return false;
    return buildAddressFingerprint(shippingAddress) !== buildAddressFingerprint(billingAddress);
  }, [shippingAddress, billingAddress]);

  const orderItems = useMemo(() => {
    if (!order) return [];
    let items = order.items;
    if (typeof items === 'string') {
      try {
        items = JSON.parse(items);
      } catch {
        items = [];
      }
    }
    if (!Array.isArray(items)) {
      items = items && typeof items === 'object' ? [items] : [];
    }
    return items;
  }, [order]);

  if (!order) return null;

  const fullName =
    shippingAddress?.name ||
    order.customerName ||
    t('ecommerceOrders.table.customerNameUnknown');
  const addressLine =
    shippingAddress?.address ||
    order.customerAddress ||
    t('ecommerceOrders.trendyolPrintModal.addressNotFound');
  const district = shippingAddress?.district || '';
  const city = shippingAddress?.city || '';
  const districtCityLine = [district, city].filter(Boolean).join(' ');
  const districtCityBold = [district, city].filter(Boolean).join('/');
  const cargoCompany =
    parsedCargoInfo?.cargoProviderName ||
    parsedCargoInfo?.cargoCompany ||
    order.shippingCompany ||
    '';
  const cargoLogoSrc = resolveCargoLogoSrc(cargoCompany);
  const logoMaxHeight = isPreview ? 42 : 36;
  const orderNumber =
    order.trendyolOrderNumber ||
    order.orderNumber ||
    order.id ||
    '-';

  const labelBoxSx = {
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: '8px',
    p: isPreview ? 1.5 : 1.1,
    bgcolor: '#fff',
    height: '100%',
    boxSizing: 'border-box' as const,
    '@media print': {
      borderRadius: '6px',
      boxShadow: 'none',
      pageBreakInside: 'avoid',
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: isPreview ? '0.88rem' : '8.5pt',
    color: TITLE_DARK,
    mb: 0.75,
    fontFamily: LABEL_FONT,
  };

  const fieldLabelSx = {
    fontWeight: 700,
    fontSize: isPreview ? '0.7rem' : '7pt',
    color: TEXT_DARK,
    minWidth: isPreview ? '68px' : '60px',
    flexShrink: 0,
    fontFamily: LABEL_FONT,
  };

  const fieldValueSx = {
    fontSize: isPreview ? '0.7rem' : '7pt',
    color: TEXT_DARK,
    lineHeight: 1.35,
    fontFamily: LABEL_FONT,
  };

  const getItemAttribute = (item: any, keys: string[]) => {
    const variantData = parseJsonObject(item?.variantData);
    for (const key of keys) {
      const direct = item?.[key];
      if (direct !== undefined && direct !== null && String(direct).trim() !== '') {
        return String(direct).trim();
      }
      const fromVariant = variantData?.[key];
      if (fromVariant !== undefined && fromVariant !== null && String(fromVariant).trim() !== '') {
        return String(fromVariant).trim();
      }
    }
    return '-';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: TY_ORANGE,
          color: 'white',
        }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <PrintIcon sx={{ mr: 1 }} />
          {t('ecommerceOrders.trendyolPrintModal.title')}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: isPreview ? '#f0f0f0' : 'white', display: 'flex', justifyContent: 'center' }}>
        <Box ref={printRef} sx={{ py: isPreview ? 2 : 0 }}>
          <Box
            className="ty-label-sheet"
            sx={{
              width: '100%',
              maxWidth: isPreview ? A5_PORTRAIT_PREVIEW_MAX_WIDTH : A5_PORTRAIT_PREVIEW_MAX_WIDTH,
              mx: 'auto',
              p: isPreview ? 1.25 : 0,
              bgcolor: 'white',
              border: `1px solid ${BORDER_COLOR}`,
              borderRadius: isPreview ? '8px' : 0,
              boxSizing: 'border-box',
              fontFamily: LABEL_FONT,
              ...a5PortraitPrintSx,
              '@media print': {
                ...a5PortraitPrintSx['@media print'],
                border: `1px solid ${BORDER_COLOR}`,
                borderRadius: '6px',
                p: '3mm',
              },
            }}
          >
            {/* Kargo şirketi uyarı bandı */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                border: `1px solid ${BORDER_COLOR}`,
                borderRadius: '6px',
                p: isPreview ? 1 : 0.75,
                mb: 1.25,
              }}
            >
              <WarningIcon sx={{ fontSize: isPreview ? 18 : 14, color: '#555', mt: 0.1, flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: isPreview ? '0.62rem' : '6pt',
                  lineHeight: 1.35,
                  color: TEXT_DARK,
                  fontFamily: LABEL_FONT,
                }}
              >
                {t('ecommerceOrders.trendyolPrintModal.cargoNotice')}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.25,
                px: 0.25,
                minHeight: logoMaxHeight + 4,
              }}
            >
              <Box
                component="img"
                src={TRENDYOL_LOGO_SRC}
                alt="Trendyol"
                sx={{
                  maxHeight: logoMaxHeight,
                  maxWidth: isPreview ? 150 : 130,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
              {cargoLogoSrc ? (
                <Box
                  component="img"
                  src={cargoLogoSrc}
                  alt={cargoCompany}
                  sx={{
                    maxHeight: logoMaxHeight,
                    maxWidth: isPreview ? 130 : 115,
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              ) : cargoCompany ? (
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: isPreview ? '0.85rem' : '8pt',
                    color: TEXT_DARK,
                    textAlign: 'right',
                    fontFamily: LABEL_FONT,
                  }}
                >
                  {cargoCompany}
                </Typography>
              ) : null}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                mb: 1,
                alignItems: 'stretch',
              }}
            >
              <Paper elevation={0} sx={labelBoxSx}>
                <Typography sx={sectionTitleSx}>
                  {t('ecommerceOrders.trendyolPrintModal.recipientInformation')}
                </Typography>

                {addressesDiffer && (
                  <Typography
                    sx={{
                      color: WARNING_RED,
                      fontSize: isPreview ? '0.58rem' : '6pt',
                      fontWeight: 600,
                      mb: 0.6,
                      lineHeight: 1.3,
                      fontFamily: LABEL_FONT,
                    }}
                  >
                    {t('ecommerceOrders.trendyolPrintModal.addressDifferenceWarning')}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                  <Box sx={{ display: 'flex', gap: 0.4 }}>
                    <Typography sx={fieldLabelSx}>
                      {t('ecommerceOrders.trendyolPrintModal.orderNoShort')} :
                    </Typography>
                    <Typography sx={fieldValueSx}>{orderNumber}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.4 }}>
                    <Typography sx={fieldLabelSx}>
                      {t('ecommerceOrders.trendyolPrintModal.fullNameShort')} :
                    </Typography>
                    <Typography sx={fieldValueSx}>{fullName}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'flex-start' }}>
                    <Typography sx={fieldLabelSx}>
                      {t('ecommerceOrders.trendyolPrintModal.address')} :
                    </Typography>
                    <Box>
                      <Typography sx={fieldValueSx}>{addressLine}</Typography>
                      {districtCityLine && (
                        <Typography sx={fieldValueSx}>{districtCityLine}</Typography>
                      )}
                      {districtCityBold && (
                        <Typography sx={{ ...fieldValueSx, fontWeight: 700 }}>{districtCityBold}</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...labelBoxSx, display: 'flex', flexDirection: 'column' }}>
                <Typography sx={sectionTitleSx}>
                  {t('ecommerceOrders.trendyolPrintModal.cargoBarcodeTitle')}
                </Typography>

                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: isPreview ? 130 : 100,
                    py: 0.25,
                  }}
                >
                  {barcodeDataUrl ? (
                    <>
                      <Box
                        component="img"
                        src={barcodeDataUrl}
                        alt={t('ecommerceOrders.trendyolPrintModal.cargoBarcodeAlt')}
                        sx={{
                          width: '100%',
                          maxWidth: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                        }}
                      />
                      <Typography
                        sx={{
                          mt: 0.5,
                          fontSize: isPreview ? '0.78rem' : '7.5pt',
                          fontWeight: 600,
                          letterSpacing: '0.3px',
                          fontFamily: 'monospace',
                          color: TEXT_DARK,
                        }}
                      >
                        {cargoBarcodeValue}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t('ecommerceOrders.trendyolPrintModal.barcodeUnavailable')}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={labelBoxSx}>
              <Typography sx={sectionTitleSx}>
                {t('ecommerceOrders.trendyolPrintModal.productInformation')}
              </Typography>

              {orderItems.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {orderItems.map((item: any, index: number) => {
                    const productName =
                      item?.name || item?.productName || t('ecommerceOrders.marketplaces.unknown');
                    const quantity = Number(item?.quantity ?? 1);
                    const color = getItemAttribute(item, ['productColor', 'color', 'renk']);
                    const size = getItemAttribute(item, ['productSize', 'size', 'beden', 'variantName']);
                    const barcode = getItemAttribute(item, ['barcode']);
                    const stockCode = getItemAttribute(item, ['merchantSku', 'stockCode', 'sku', 'n11StockCode']);

                    const productColumns = [
                      {
                        label: t('ecommerceOrders.trendyolPrintModal.quantityLabel'),
                        value: `${quantity} ${t('ecommerceOrders.trendyolPrintModal.quantityUnit')}`,
                      },
                      { label: t('ecommerceOrders.trendyolPrintModal.colorLabel'), value: color },
                      { label: t('ecommerceOrders.trendyolPrintModal.sizeLabel'), value: size },
                      { label: t('ecommerceOrders.trendyolPrintModal.barcodeLabel'), value: barcode },
                      { label: t('ecommerceOrders.trendyolPrintModal.stockCodeLabel'), value: stockCode },
                    ];

                    return (
                      <Box
                        key={`${item?.id || item?.sku || index}-${index}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.75,
                          borderTop: index > 0 ? `1px solid ${BORDER_COLOR}` : 'none',
                          pt: index > 0 ? 0.75 : 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            border: `1px solid ${BORDER_COLOR}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontSize: isPreview ? '0.65rem' : '6.5pt',
                            fontWeight: 600,
                            color: TEXT_DARK,
                          }}
                        >
                          {index + 1}
                        </Box>

                        <Typography
                          sx={{
                            flex: '0 0 28%',
                            minWidth: 70,
                            maxWidth: 110,
                            fontWeight: 700,
                            fontSize: isPreview ? '0.72rem' : '7pt',
                            color: TEXT_DARK,
                            lineHeight: 1.25,
                            fontFamily: LABEL_FONT,
                          }}
                        >
                          {productName}
                        </Typography>

                        <Box
                          sx={{
                            flex: 1,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                            gap: 0.5,
                          }}
                        >
                          {productColumns.map((column) => (
                            <Box key={column.label}>
                              <Typography
                                sx={{
                                  fontSize: isPreview ? '0.55rem' : '5.5pt',
                                  color: LABEL_GREY,
                                  lineHeight: 1.15,
                                  mb: 0.15,
                                  fontFamily: LABEL_FONT,
                                }}
                              >
                                {column.label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: isPreview ? '0.65rem' : '6.5pt',
                                  fontWeight: 700,
                                  color: TEXT_DARK,
                                  lineHeight: 1.2,
                                  wordBreak: 'break-word',
                                  fontFamily: LABEL_FONT,
                                }}
                              >
                                {column.value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {t('ecommerceOrders.trendyolPrintModal.noProducts')}
                </Typography>
              )}
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      {isPreview && (
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={onClose} variant="outlined" startIcon={<CloseIcon />}>
            {t('ecommerceOrders.orderDetailsModal.close')}
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            startIcon={<PrintIcon />}
            sx={{
              bgcolor: TY_ORANGE,
              '&:hover': { bgcolor: TY_ORANGE_DARK },
            }}
          >
            {t('ecommerceOrders.trendyolPrintModal.print')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TrendyolPrintModal;
